"use client";

/**
 * DiscoveryBannerPage 컴포넌트
 * @description 배너 한 장의 내용 — 그림, 그리고 링크. 배너의 목적지는 **바깥 링크**일 수
 *   있다(서버가 주는 값이라 앱 라우트라는 보장이 없다) — 절대 주소면 새 창으로, 아니면 앱 안
 *   링크로 간다. 링크가 없는 배너는 그림만 선다. 제목은 눈에 보이지 않고 읽어주는 이름이다.
 */
import Link from "next/link";
import { memo, useCallback } from "react";

import { discoveryBannerStyles } from "./DiscoveryBanner.styles";
import type { DiscoveryBannerPageProps } from "./DiscoveryBanner.types";

import { trackBannerClicked } from "../../analytics";
import { AppImage, HEADER_IMAGE_PLACEHOLDER } from "@/shared/ui";

/** 콘텐츠 열 최대 폭(1280)에서 좌우 여백을 뺀 값 — 배너가 가장 넓게 서는 폭이다 */
const SIZES = "(min-width: 1280px) 1216px, 100vw";

const isExternal = (url: string): boolean => /^https?:\/\//.test(url);

const DiscoveryBannerPage = memo<DiscoveryBannerPageProps>(
  ({ banner, index }) => {
    const track = useCallback(
      () => trackBannerClicked(banner, index),
      [banner, index],
    );
    const image = (
      <AppImage
        uri={banner.imageUrl}
        fallback={HEADER_IMAGE_PLACEHOLDER}
        altText={banner.title}
        sizes={SIZES}
        priority={index === 0}
      />
    );

    if (!banner.linkUrl) {
      return <div className={discoveryBannerStyles.page}>{image}</div>;
    }
    if (isExternal(banner.linkUrl)) {
      return (
        <a
          href={banner.linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={track}
          className={discoveryBannerStyles.page}
          aria-label={banner.title}
          draggable={false}
        >
          {image}
        </a>
      );
    }
    return (
      <Link
        href={banner.linkUrl}
        onClick={track}
        className={discoveryBannerStyles.page}
        aria-label={banner.title}
        draggable={false}
      >
        {image}
      </Link>
    );
  },
);

DiscoveryBannerPage.displayName = "DiscoveryBannerPage";

export default DiscoveryBannerPage;
